/**
 * Effective RTP, per selection — the single source both RTP tables read.
 *
 * Effective RTP is MULTIPLICATIVE on base RTP: a Lightning Round scales the
 * payout multiplier, so E[RTP] = base x E[boost factor]. The retired
 * percentage-point fudges (base+1/+2.5 in FullRtpTable, +1..+8 by probability
 * bucket in SingleRtpTable) were wrong in both directions and disagreed with
 * each other — the bucket version put Survivor at up to 102.34%, advertising
 * an above-100% RTP.
 *
 *   Effective(s) = base_s x (1 + Sum_tiers P_t . q_t(s) . (E[B_t] - 1))
 *
 *   P_t  fire probability of tier t. Tiers are checked rarest-first and the
 *        first hit wins, so P_t = (1/f_t) . Prod_{rarer t'} (1 - 1/f_t').
 *   E[B] weighted mean of the tier's boost options.
 *   q_t  share of that tier's boost pool the bet covers — k/N when
 *        unrestricted, min(k, pool)/pool when the tier has a restricted pool.
 *
 * Quoted at player level 0. Player Level adds up to a further 1% on top.
 *
 * PORTED VERBATIM from 155-status-page/scripts/effective-rtp.mjs, which
 * generates the `rtpEffective` figures in the status Supabase `game_pricing`
 * table. `effectiveRtp.test.ts` asserts this file reproduces every one of
 * those production figures exactly — that test is the cross-check, so keep the
 * two implementations in step.
 *
 * SOURCE OF TRUTH for the tiers below:
 *   marble games  155-game-engine/games.py
 *   CCTV games    irl-vehicle-counter/games/<game>/__init__.py
 *   crash         155-game-crash/games.py  (no lightning block)
 * Those repos deploy manually, so re-check this file whenever tiers are
 * retuned — a retune silently invalidates every published figure.
 */

export type SideBetType =
  | 'StraightForecast'
  | 'ReverseForecast'
  | 'Tricast'
  | 'CombinationTricast'

type BoostOption = readonly [multiplier: number, weight: number]

const BOOSTS: Record<string, readonly BoostOption[]> = {
  legendary: [
    [3, 5],
    [4, 3],
    [5, 2]
  ],
  epic: [
    [2, 5],
    [3, 3],
    [4, 2]
  ],
  rare: [
    [1.5, 5],
    [2, 3],
    [3, 2]
  ],
  common: [
    [1.1, 6],
    [1.2, 3],
    [1.5, 1]
  ]
}

const expectedBoost = (options: readonly BoostOption[] | undefined): number => {
  if (!options || options.length === 0) return 1
  const w = options.reduce((s, [, weight]) => s + weight, 0)
  if (w === 0) return 1
  return options.reduce((s, [mult, weight]) => s + mult * weight, 0) / w
}

/** Tier frequency families, rarest-first. */
const FAMILIES: Record<string, ReadonlyArray<readonly [string, number]>> = {
  marble: [
    ['legendary', 200],
    ['epic', 60],
    ['rare', 25],
    ['common', 3]
  ],
  restricted: [
    ['legendary', 600],
    ['epic', 200],
    ['rare', 120],
    ['common', 3]
  ],
  cctv: [
    ['legendary', 1000],
    ['epic', 400],
    ['rare', 60],
    ['common', 3]
  ]
}

/** Per-tier fire probability: rarest-first, first hit wins. */
function tierProbabilities(family: string): Array<{ p: number; eb: number }> {
  const tiers = FAMILIES[family] ?? []
  const out: Array<{ p: number; eb: number }> = []
  let survive = 1
  for (const [name, freq] of tiers) {
    out.push({ p: survive * (1 / freq), eb: expectedBoost(BOOSTS[name]) })
    survive *= 1 - 1 / freq
  }
  return out
}

interface LightningShape {
  /** N selectable outcomes in the main market. */
  outcomes?: number
  /** Tier frequency family. */
  family?: string
  /** True when `eligible_outcomes` restricts the boost pool. */
  restricted?: boolean
  /** selection -> how many of the restricted pools it belongs to. */
  tiersFor?: Record<string, number>
  /** Selections whose ids are never in the boost map. */
  noBoost?: readonly string[]
  /** True when the game has no lightning configured at all. */
  none?: boolean
}

const LIGHTNING: Record<string, LightningShape> = {
  marble_plinko: { outcomes: 8, family: 'marble' },
  rolling_dunes: { outcomes: 8, family: 'marble' },
  snake: { outcomes: 6, family: 'marble' },
  fast_lane: { outcomes: 10, family: 'marble' },

  // Restricted pools: legendary 1, epic 2, rare 3, common all.
  stairpong: {
    outcomes: 5,
    family: 'restricted',
    restricted: true,
    tiersFor: { pink: 3, blue: 2, green: 1, white: 0, orange: 0 }
  },
  survivor: {
    outcomes: 5,
    family: 'restricted',
    restricted: true,
    tiersFor: { white: 3, green: 2, inferno: 1, bee: 0, neptune: 0 }
  },
  fish_tank: {
    outcomes: 5,
    family: 'restricted',
    restricted: true,
    tiersFor: { red: 3, white: 2, chest: 1, left: 0, right: 0 }
  },

  // CCTV: pools jackpot | +over | +under | all four. Exact Low/High are a
  // separate market whose ids are never in the boost map, so they get nothing.
  rush_hour: {
    outcomes: 4,
    family: 'cctv',
    restricted: true,
    tiersFor: { exact: 3, over: 2, under: 1, range: 0 },
    noBoost: ['exact low', 'exact high']
  },
  duck_river: {
    outcomes: 4,
    family: 'cctv',
    restricted: true,
    tiersFor: { exact: 3, over: 2, under: 1, range: 0 },
    noBoost: ['exact low', 'exact high']
  },
  snow_run: {
    outcomes: 4,
    family: 'cctv',
    restricted: true,
    tiersFor: { exact: 3, over: 2, under: 1, range: 0 },
    noBoost: ['exact low', 'exact high']
  },

  // Birdie Time: the marble tiers (155-game-birdie games.py, both envs), ONE unrestricted pool
  // of twelve — the ten putt options plus Frost and Fire (owner, 2026-09-21). Base 94% since
  // 2026-09-21; every option carries the same lift, 1/12 of the pool's expected boost.
  birdie_time: { outcomes: 12, family: 'marble' },

  // No lightning configured; effective equals base.
  coin_flip: { none: true },
  crash: { none: true }
}

/**
 * Race-configuration id -> game key, for BOTH environments.
 *
 * The widget's `game.id` IS the race-configuration id, which is what
 * `game_pricing.production_id` / `.staging_id` key on. Track ids differ
 * between staging and production and `track.game_type` is not a game identity
 * at all — six production tracks share `marbles` — so neither can be used
 * here. Footfall deliberately shares one id across environments.
 *
 * Regenerate with:
 *   select game_key, production_id, staging_id from game_pricing;
 */
const GAME_KEY_BY_RACE_CONFIG_ID: Record<string, string> = {
  // birdie_time (not in game_pricing yet — ids from 155-game-birdie games.py, 2026-09-21)
  '56fc2399-7f30-41e0-820d-78432bbd084f': 'birdie_time',
  '9285b367-3043-4981-bf4b-69f399d2c24c': 'birdie_time',
  // coin_flip
  'e4c85f24-3703-46bc-933a-b9e54d3aaecb': 'coin_flip',
  'd20ba3f9-4569-498a-8c00-0c9912bc9390': 'coin_flip',
  // crash (Footfall — same id in both environments)
  'f42795eb-a884-448f-bb42-6bf5a2fb3e33': 'crash',
  // duck_river
  '76be9102-f4d5-499e-adef-7a6f6848ccf2': 'duck_river',
  '3a4b54ae-7571-4211-bda2-9eb5f5df36c3': 'duck_river',
  // fast_lane
  '3f5d6fa5-346b-4783-be3c-8e40ef288759': 'fast_lane',
  'd67ac242-f94c-451a-a724-558c1d609325': 'fast_lane',
  // fish_tank
  'a8c81652-cbd4-4b5d-94cd-5585715a6133': 'fish_tank',
  '27e2135b-75ee-4872-8b29-7f96f310d239': 'fish_tank',
  // marble_plinko
  '2469f711-2da6-46b7-8648-3313dfdc5bb5': 'marble_plinko',
  '64f65d3b-3c9b-4a57-96de-6bed459ab518': 'marble_plinko',
  // rolling_dunes
  '27948be8-eae7-4276-823f-24c79247a002': 'rolling_dunes',
  '0e4b84e0-13aa-4782-b80e-d539d65fd91f': 'rolling_dunes',
  // rush_hour
  'e30b58a2-653f-492a-81f1-8f1769c4d31c': 'rush_hour',
  '3d80d6ab-0825-4e3a-8b1d-91ff0631e3fe': 'rush_hour',
  // snake
  'e1b186ee-b609-40e9-9949-ee20b65ffdaf': 'snake',
  'ad3cfb94-180e-4669-bc85-e2a29753f490': 'snake',
  // snow_run
  '584b1f3d-8338-4f73-af6b-0b9ac74275eb': 'snow_run',
  '7f074d19-cfa9-4e94-96bb-b3d53a5dea67': 'snow_run',
  // stairpong
  'f38c69e6-f784-4b2d-8791-309432b0eb39': 'stairpong',
  '023f1d90-746c-4d10-b7e3-39d579f59a1f': 'stairpong',
  // survivor
  'b07d5391-09c7-403d-9695-3702503f9f0c': 'survivor',
  '6011911c-8f07-44c5-80d8-d109b015b1e8': 'survivor'
}

/**
 * Resolve the pricing game key from the widget's race-configuration id.
 * Returns null for a game we have no verified pricing for — callers must
 * fall back to base figures rather than inventing a boost.
 */
export const resolveGameKey = (raceConfigurationId: string | undefined | null): string | null =>
  (raceConfigurationId && GAME_KEY_BY_RACE_CONFIG_ID[raceConfigurationId]) || null

/**
 * Forecast / tricast margin, as the surviving fraction of the fair odds.
 *
 * These mirror the Go domain CalculationService that actually settles bets
 * (`internal/game/domain/services/calculation.go`), whose defaults are
 * StraightForecast 0.12, ReverseForecast 0.10, Tricast 0.14 and
 * CombinationTricast 0.12 — i.e. the factors below. A game may override them
 * via its DynamoDB `MarginConfig`; Marble Plinko is the only one that does,
 * flattening all four to 0.15 (a 0.85 factor). The widget used to hardcode
 * Marble Plinko's override for every game, which under-quoted Rolling Dunes.
 */
const DEFAULT_SIDE_BET_MARGIN: Record<SideBetType, number> = {
  StraightForecast: 0.88,
  ReverseForecast: 0.9,
  Tricast: 0.86,
  CombinationTricast: 0.88
}

export const SIDE_BET_MARGIN: Record<string, Record<SideBetType, number>> = {
  marble_plinko: {
    StraightForecast: 0.85,
    ReverseForecast: 0.85,
    Tricast: 0.85,
    CombinationTricast: 0.85
  },
  rolling_dunes: { ...DEFAULT_SIDE_BET_MARGIN },
  snake: { ...DEFAULT_SIDE_BET_MARGIN }
}

const marginFor = (gameKey: string | null, betType: SideBetType): number =>
  (gameKey && SIDE_BET_MARGIN[gameKey]?.[betType]) || DEFAULT_SIDE_BET_MARGIN[betType]

/** Number of distinct winning combinations for a side bet over n outcomes. */
const combinations = (betType: SideBetType, n: number): number => {
  if (n < 2) return 0
  switch (betType) {
    case 'StraightForecast':
      return n * (n - 1)
    case 'ReverseForecast':
      return (n * (n - 1)) / 2
    case 'Tricast':
      return n < 3 ? 0 : n * (n - 1) * (n - 2)
    case 'CombinationTricast':
      return n < 3 ? 0 : (n * (n - 1) * (n - 2)) / 6
    default:
      return 0
  }
}

/**
 * Side-bet payout multiplier over `n` bettable outcomes.
 *
 * `n` MUST be the number of bettable options in the main market, not
 * `race_configuration.total_marbles` — Snake reports 8 total marbles but only
 * has 6 outcomes, and pricing its forecasts off 8 would over-quote them.
 */
export const sideBetMultiplier = (
  gameKey: string | null,
  betType: SideBetType,
  n: number
): number => combinations(betType, n) * marginFor(gameKey, betType)

/**
 * Side-bet base RTP as a percentage. Payout is (1/p) x margin against a
 * probability of p, so the RTP is just the margin — independent of n.
 */
export const sideBetRtp = (gameKey: string | null, betType: SideBetType): number =>
  round2(marginFor(gameKey, betType) * 100)

/** How many items a bet of this type selects. */
export function selectionSize(label = ''): number {
  const l = label.toLowerCase()
  if (l.includes('tricast')) return 3
  if (l.includes('forecast')) return 2
  return 1
}

/**
 * Effective RTP for one selection, as a percentage.
 *
 * `label` is the selection or bet-type label — for main markets that is the
 * marble colour (which is what the boost map keys on), for side bets the bet
 * type. Unknown games fall through to base RTP: never invent a lift.
 */
export function effectiveRtp(
  gameKey: string | null,
  baseRtp: number | null | undefined,
  label = '',
  k: number = selectionSize(label)
): number | null {
  if (baseRtp === null || baseRtp === undefined) return null

  const cfg = gameKey ? LIGHTNING[gameKey] : undefined
  if (!cfg || cfg.none) return round2(baseRtp)

  const l = label.toLowerCase()
  if (cfg.noBoost?.includes(l)) return round2(baseRtp)

  const tiers = tierProbabilities(cfg.family ?? '')
  const outcomes = cfg.outcomes ?? 0

  let lift = 0
  if (cfg.restricted) {
    // Restricted pools are sized 1, 2, 3 for legendary/epic/rare; common is
    // the full field. A selection is in the r rarest pools, where r is its rank.
    const rank = cfg.tiersFor?.[l] ?? 0
    const poolSizes = [1, 2, 3]
    tiers.forEach((t, i) => {
      const inPool = i < 3 ? rank >= 3 - i : true
      const pool = (i < 3 ? poolSizes[i] : outcomes) ?? 0
      if (inPool && pool > 0) lift += t.p * (Math.min(k, pool) / pool) * (t.eb - 1)
    })
  } else {
    if (outcomes <= 0) return round2(baseRtp)
    for (const t of tiers) lift += t.p * (k / outcomes) * (t.eb - 1)
  }

  return round2(baseRtp * (1 + lift))
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Does this game actually have Lightning Rounds? */
export const hasLightning = (gameKey: string | null | undefined): boolean => {
  const cfg = gameKey ? LIGHTNING[gameKey] : undefined
  return !!cfg && !cfg.none
}
